import { memo, useCallback, useMemo, useState } from 'react';

import { CheckIcon, XIcon } from '@air/design-icons';
import { Chip } from '@air/primitive-chip';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputTrigger,
  type ComboboxInputTriggerProps,
  type ComboboxInputTriggerVariants,
  ComboboxItem,
  ComboboxList,
  ComboboxLoading,
} from '@air/primitive-combobox';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { type TagBase, type TagSelectOption } from '~/components/Tags/utils';

export type TagsSelectProps = {
  id?: string;
  defaultOpen?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onCreateTag?: (name: string) => void;
  onInputChange: (value: string) => void;
  onTagsSelectionChange: (newTags: TagSelectOption[]) => void;
  placeholder?: string;
  placeholderClassName?: string;
  selectedTags: TagSelectOption[];
  size?: ComboboxInputTriggerVariants['size'];
  tags: TagBase[];
  hideChipsInInput?: boolean;
} & Pick<ComboboxInputTriggerProps, 'className'>;

export const TagsSelect = memo(
  ({
    className,
    defaultOpen,
    disabled = false,
    id = 'tag',
    tags,
    onCreateTag: _onCreateTag,
    onInputChange,
    onTagsSelectionChange,
    isLoading = false,
    selectedTags = [],
    size = 'large',
    placeholder = 'Search for tags',
    placeholderClassName,
    hideChipsInInput = false,
  }: TagsSelectProps) => {
    const [searchValue, setSearchValue] = useState('');
    const onValueChange = useCallback(
      (value: string) => {
        setSearchValue(value);
        onInputChange(value);
      },
      [onInputChange],
    );

    const onCreateTag = useCallback(
      (value: string) => {
        _onCreateTag?.(value);
        onValueChange('');
      },
      [_onCreateTag, onValueChange],
    );

    const onRemoveTag = useCallback(
      (tag: TagSelectOption) => {
        // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onTagsSelectionChange?.(selectedTags.filter((t) => t.value !== tag.value));
      },
      [onTagsSelectionChange, selectedTags],
    );

    const onTagClick = useCallback(
      (tag: TagBase) => {
        if (selectedTags.some((t) => t.value === tag.id)) {
          onRemoveTag({ value: tag.id, label: tag.label });
        } else {
          // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
          onTagsSelectionChange?.([...selectedTags, { value: tag.id, label: tag.label }]);
        }
        onValueChange('');
      },
      [onRemoveTag, onTagsSelectionChange, selectedTags, onValueChange],
    );

    const showCreateTag = useMemo(
      () => searchValue && !tags.some((tag) => tag.label === searchValue) && !isLoading,
      [tags, searchValue, isLoading],
    );

    return (
      <Combobox defaultOpen={defaultOpen}>
        <ComboboxInputTrigger asChild className={className} data-id={id} disabled={disabled} size={size}>
          <div>
            {hideChipsInInput || selectedTags.length === 0 ? (
              <div className={cn('ads:text-placeholder text-gray-10', placeholderClassName)}>{placeholder}</div>
            ) : (
              selectedTags.map((tag) => (
                <Chip data-id={tag.value} key={tag.value} shape="rounded" size="medium">
                  <span className="truncate">{tag.label}</span>
                  <IconButton
                    adsIcon
                    appearance="ghost"
                    color="gray"
                    data-testid="REMOVE_TAG_BUTTON"
                    data-id={tag.value}
                    icon={XIcon}
                    label="Remove smart tag"
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveTag(tag);
                    }}
                    size="extra-small"
                  />
                </Chip>
              ))
            )}
          </div>
        </ComboboxInputTrigger>
        <ComboboxContent
          className="max-h-[min(320px,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)]"
          sideOffset={4}
          shouldFilter={false}
        >
          <ComboboxInput onValueChange={onValueChange} placeholder="Search tags..." value={searchValue} />

          {isLoading ? <ComboboxLoading>Loading...</ComboboxLoading> : <ComboboxEmpty>No tags found.</ComboboxEmpty>}

          <ComboboxList>
            {tags.map((tag) => {
              const isSelected = selectedTags.some((t) => t.value === tag.id);

              return (
                // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                <ComboboxItem data-id={tag.id} onSelect={() => onTagClick(tag)} key={tag.id}>
                  {isSelected ? (
                    <CheckIcon className="shrink-0 ads:text-icon-primary text-blue-9" size="sm" />
                  ) : (
                    <div className="size-4 shrink-0" />
                  )}
                  <div className={cn('truncate', isSelected ? 'opacity-70' : '')}>{tag.label}</div>
                </ComboboxItem>
              );
            })}
            {!!showCreateTag && onCreateTag && (
              // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
              <ComboboxItem data-id="create-tag" onSelect={() => onCreateTag(searchValue)}>
                {!!tags.length && <div className="size-4 shrink-0" />}
                <span className="truncate">Create &quot;{searchValue}&quot;?</span>
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  },
);

TagsSelect.displayName = 'TagsSelect';
